import React, { useMemo } from 'react';
import { Layout, Menu, Button, Drawer, Grid, Modal, Steps, Tour, Typography, Carousel } from 'antd';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { canViewInTopBar, isMasterUser, getUserRoleCode } from '../../helpers/accessControl';
import { TOP_BAR_ITEM_AND_RESTRICTIONS } from '../../helpers/viewConfig';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { isVehicleDevice, type DeviceItem } from '../Operations/missionModels';
import { DashboardOutlined, RobotOutlined, AppstoreOutlined, DeploymentUnitOutlined, ExperimentOutlined, LogoutOutlined, FundProjectionScreenOutlined, FileTextOutlined, SettingOutlined, SwapOutlined, UnorderedListOutlined, MenuOutlined, CreditCardOutlined, QuestionCircleOutlined, SlidersOutlined, PlayCircleOutlined } from '@ant-design/icons';
import logoImage from '../../static/ColourIconSecondary.png';
import { buildOnboardingStorageKey, mergeOnboardingState, readOnboardingState } from '../../helpers/onboardingState';
import axios from 'axios';
import '../../style/Layout.css';

const { Content, Sider, Header } = Layout;
const { Text } = Typography;

const ROUTE_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/reports': 'Reports',
  '/saved-reports': 'Saved reports',
  '/saved-reports/:id': 'Saved report',
  '/measurement-list': 'Measurement list',
  '/devices': 'Devices',
  '/operations': 'Operations',
  '/slam-maps': 'SLAM maps',
  '/assistant': 'Assistant',
  '/areas': 'Areas',
  '/areas/new': 'Areas',
  '/areas/:id/edit': 'Areas',
  '/patterns': 'Patterns',
  '/patterns?mode=template': 'Patterns',
  '/patterns?mode=gps': 'Patterns',
  '/patterns/create/template': 'Patterns',
  '/patterns/create/area': 'Patterns',
  '/measurements': 'Measurement setup',
  '/measurements/:id': 'Measurement detail',
  '/measurements/assign-areas': 'Measurement assign areas',
  '/measurements/assign-patterns': 'Measurement assign patterns',
  '/measurements/:id/edit-location': 'Measurement location',
  '/configuration': 'Configuration',
  '/configuration?tab=profile': 'Configuration - Profile',
  '/configuration?tab=users': 'Configuration - User Management',
  '/configuration?tab=company': 'Configuration - Company',
  '/configuration?tab=subscription': 'Configuration - Subscription',
  '/configuration?tab=new-company': 'Configuration - New Company',
  '/docs/integrations': 'Integration Wiki',
  '/back-office?tab=subscriptions': 'Back Office - Subscriptions',
  '/back-office?tab=boundaries': 'Back Office - Operating Boundaries',
  '/back-office?tab=devices': 'Back Office - Devices',
  '/back-office?tab=modules': 'Back Office - Device Modules and Capabilities',
  '/back-office?tab=area-types': 'Back Office - Area Types',
  '/back-office?tab=ranges': 'Back Office - Ranges',
  '/back-office?tab=report': 'Back Office - Report',
  '/back-office?tab=agents': 'Back Office - Agents',
};

interface HelpMediaSlide {
  id: string;
  title: string;
  description: string;
  type: 'video' | 'image';
  src: string;
  mimeType?: string;
  poster?: string;
}

const HELP_MEDIA_FALLBACK_POSTER = '/shape.png';

const HELP_MEDIA_LIBRARY: Record<string, HelpMediaSlide[]> = {
  dashboard: [
    {
      id: 'dashboard-overview',
      title: 'Dashboard overview',
      description: 'Quickly review warnings, latest measurement, and favorite area cards.',
      type: 'video',
      src: '/help/dashboard/overview.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'dashboard-actions',
      title: 'Drill into details',
      description: 'Use dashboard cards and shortcuts to navigate directly to investigation pages.',
      type: 'video',
      src: '/help/dashboard/actions.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  reports: [
    {
      id: 'reports-build',
      title: 'Build report views',
      description: 'Select area, range, and sections to generate report insights.',
      type: 'video',
      src: '/help/reports/build-report.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'reports-save',
      title: 'Save and reuse reports',
      description: 'Save report configurations for repeatable analysis and sharing.',
      type: 'video',
      src: '/help/reports/save-report.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  measurementList: [
    {
      id: 'measurement-list-filter',
      title: 'Filter measurement list',
      description: 'Use filters and sorting to find the exact measurement quickly.',
      type: 'video',
      src: '/help/measurement-list/filter.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'measurement-list-open',
      title: 'Open analysis flow',
      description: 'Jump from list rows to detail and analysis pages.',
      type: 'video',
      src: '/help/measurement-list/open-analysis.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  areas: [
    {
      id: 'areas-manage',
      title: 'Create and manage areas',
      description: 'Add or edit areas and keep structure consistent for reporting.',
      type: 'video',
      src: '/help/areas/manage.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'areas-hierarchy',
      title: 'Use area hierarchy',
      description: 'Navigate parent-child areas for cleaner organization and analysis.',
      type: 'video',
      src: '/help/areas/hierarchy.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  patterns: [
    {
      id: 'patterns-create',
      title: 'Create patterns',
      description: 'Build template or GPS-driven patterns for repeatable capture plans.',
      type: 'video',
      src: '/help/patterns/create.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'patterns-assign',
      title: 'Assign and validate points',
      description: 'Link pattern points to areas and validate placement.',
      type: 'video',
      src: '/help/patterns/assign-points.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  measurements: [
    {
      id: 'measurements-capture',
      title: 'Capture measurements',
      description: 'Create measurements from image/text/location workflows.',
      type: 'video',
      src: '/help/measurements/capture.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'measurements-link',
      title: 'Link measurements to area/pattern',
      description: 'Assign measurements correctly to support area and pattern reporting.',
      type: 'video',
      src: '/help/measurements/link.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  assistant: [
    {
      id: 'assistant-ask',
      title: 'Ask the assistant effectively',
      description: 'Use clear prompts and context to get precise analysis responses.',
      type: 'video',
      src: '/help/assistant/ask.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  devices: [
    {
      id: 'devices-overview',
      title: 'Manage devices',
      description: 'Review assigned devices and keep operational status up to date.',
      type: 'video',
      src: '/help/devices/manage.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  configuration: [
    {
      id: 'configuration-profile',
      title: 'Configure profile and integration',
      description: 'Set user profile and communication channel settings.',
      type: 'video',
      src: '/help/configuration/profile.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
    {
      id: 'configuration-subscription',
      title: 'Review subscription settings',
      description: 'Check limits and enabled features for your current company.',
      type: 'video',
      src: '/help/configuration/subscription.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  backOffice: [
    {
      id: 'backoffice-controls',
      title: 'Back Office controls',
      description: 'Manage subscriptions, area types, ranges, and agents.',
      type: 'video',
      src: '/help/back-office/controls.webm',
      mimeType: 'video/webm',
      poster: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
  generic: [
    {
      id: 'generic-help',
      title: 'View walkthrough',
      description: 'Open the sidebar tour replay for step-by-step guidance in this view.',
      type: 'image',
      src: HELP_MEDIA_FALLBACK_POSTER,
    },
  ],
};

const resolveHelpMediaGroupKey = (routeKey: string): keyof typeof HELP_MEDIA_LIBRARY => {
  if (!routeKey) return 'generic';
  if (routeKey === '/dashboard') return 'dashboard';
  if (routeKey === '/assistant') return 'assistant';
  if (routeKey === '/devices') return 'devices';
  if (routeKey === '/measurement-list') return 'measurementList';
  if (routeKey === '/reports' || routeKey.startsWith('/saved-reports')) return 'reports';
  if (routeKey.startsWith('/areas')) return 'areas';
  if (routeKey.startsWith('/patterns')) return 'patterns';
  if (routeKey.startsWith('/measurements')) return 'measurements';
  if (routeKey.startsWith('/configuration')) return 'configuration';
  if (routeKey.startsWith('/back-office')) return 'backOffice';
  return 'generic';
};

const normalizeRouteKey = (
  pathname: string,
  search: string,
  backOfficeTab: string | null,
  patternsTourMode: 'template' | 'gps' | null
): string => {
  if (pathname.startsWith('/back-office')) {
    return `/back-office?tab=${backOfficeTab || 'subscriptions'}`;
  }
  if (pathname === '/configuration') {
    const tab = new URLSearchParams(search).get('tab') || 'profile';
    const allowedTabs = ['profile', 'users', 'company', 'subscription', 'new-company'];
    return `/configuration?tab=${allowedTabs.includes(tab) ? tab : 'profile'}`;
  }
  if (pathname === '/patterns') {
    if (patternsTourMode === 'template') return '/patterns?mode=template';
    if (patternsTourMode === 'gps') return '/patterns?mode=gps';
  }
  if (/^\/saved-reports\/\d+/.test(pathname)) {
    return '/saved-reports/:id';
  }
  if (/^\/areas\/\d+\/edit$/.test(pathname)) {
    return '/areas/:id/edit';
  }
  if (/^\/measurements\/\d+\/edit-location$/.test(pathname)) {
    return '/measurements/:id/edit-location';
  }
  if (/^\/measurements\/\d+$/.test(pathname)) {
    return '/measurements/:id';
  }
  return pathname;
};

interface LayoutComponentProps {
  children: React.ReactNode;
}

interface OnboardingStepItem {
  title: string;
  description: React.ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}

type LayoutTourStep = NonNullable<React.ComponentProps<typeof Tour>['steps']>[number];
interface TourStepDefinition {
  id: string;
  title: string;
  description: React.ReactNode;
  selector?: string;
  placement?: LayoutTourStep['placement'];
}
interface ActiveTourStep {
  id: string;
  step: LayoutTourStep;
}

const resolveTourTargetElement = (selector: string): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  const element = document.querySelector(selector);
  return element instanceof HTMLElement ? element : null;
};

const LayoutComponent: React.FC<LayoutComponentProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [viewportHeight, setViewportHeight] = React.useState(() =>
    (typeof window !== 'undefined' ? window.innerHeight : 0)
  );

  React.useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isLandscapeShort = viewportHeight > 0 && viewportHeight <= 600;
  const isMobile = !screens.md || (!screens.lg && isLandscapeShort);
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  React.useEffect(() => {
    if (!isMobile) {
      setMobileNavOpen(false);
    }
  }, [isMobile]);

  const isBackOfficeRoute = location.pathname.startsWith('/back-office');

  const backOfficeTab = useMemo(() => {
    if (!isBackOfficeRoute) return null;
    if (location.pathname.startsWith('/back-office/area-types')) {
      return 'area-types';
    }
    if (location.pathname.startsWith('/back-office/metrics')) {
      return 'report';
    }
    const tab = new URLSearchParams(location.search).get('tab') || 'subscriptions';
    const allowedTabs = ['subscriptions', 'boundaries', 'devices', 'modules', 'area-types', 'ranges', 'report', 'agents'];
    return allowedTabs.includes(tab) ? tab : 'subscriptions';
  }, [isBackOfficeRoute, location.pathname, location.search]);

  const [patternsTourMode, setPatternsTourMode] = React.useState<'template' | 'gps' | null>(null);

  React.useEffect(() => {
    const handlePatternsTourMode = (event: Event) => {
      const custom = event as CustomEvent<{ mode?: string }>;
      const mode = custom.detail?.mode;
      if (mode === 'template' || mode === 'gps') {
        setPatternsTourMode(mode);
      }
    };

    window.addEventListener('oscar:patterns-tour-mode', handlePatternsTourMode as EventListener);
    return () => {
      window.removeEventListener('oscar:patterns-tour-mode', handlePatternsTourMode as EventListener);
    };
  }, []);

  const currentCompanyId = currentUser?.currentCompanyId || currentUser?.companyId || null;
  const [selectedCompanyHasVehicle, setSelectedCompanyHasVehicle] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    const loadSelectedCompanyVehicles = async () => {
      if (!currentCompanyId || isBackOfficeRoute) {
        setSelectedCompanyHasVehicle(false);
        return;
      }

      try {
        const response = await axios.get(ENDPOINTS.GET_DEVICES, { withCredentials: true });
        if (cancelled) return;

        const devices: DeviceItem[] = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];
        setSelectedCompanyHasVehicle(devices.some(isVehicleDevice));
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to check selected company vehicle devices:', error);
          setSelectedCompanyHasVehicle(false);
        }
      }
    };

    void loadSelectedCompanyVehicles();

    return () => {
      cancelled = true;
    };
  }, [currentCompanyId, isBackOfficeRoute]);

  const onboardingStorageKey = useMemo(
    () => buildOnboardingStorageKey(currentUser?.id, currentCompanyId),
    [currentUser?.id, currentCompanyId]
  );
  const currentRouteKey = useMemo(
    () => normalizeRouteKey(location.pathname, location.search, backOfficeTab, patternsTourMode),
    [location.pathname, location.search, backOfficeTab, patternsTourMode]
  );
  const [onboardingOpen, setOnboardingOpen] = React.useState(false);
  const [onboardingStepIndex, setOnboardingStepIndex] = React.useState(0);
  const [tourOpen, setTourOpen] = React.useState(false);
  const [tourRouteKey, setTourRouteKey] = React.useState<string | null>(null);
  const [tourMode, setTourMode] = React.useState<'auto' | 'manual'>('auto');
  const [activeTourSteps, setActiveTourSteps] = React.useState<ActiveTourStep[]>([]);
  const [tourCurrentStepIndex, setTourCurrentStepIndex] = React.useState(0);
  const tourShownStepIdsRef = React.useRef<Set<string>>(new Set());
  const tourCompletedStepIdsRef = React.useRef<Set<string>>(new Set());
  const [helpModalOpen, setHelpModalOpen] = React.useState(false);
  const [failedHelpMediaIds, setFailedHelpMediaIds] = React.useState<Record<string, boolean>>({});

  const helpMediaGroupKey = useMemo(
    () => resolveHelpMediaGroupKey(currentRouteKey),
    [currentRouteKey]
  );
  const activeHelpSlides = useMemo(
    () => HELP_MEDIA_LIBRARY[helpMediaGroupKey] || HELP_MEDIA_LIBRARY.generic,
    [helpMediaGroupKey]
  );
  const activeRouteLabel = ROUTE_LABELS[currentRouteKey] || ROUTE_LABELS[location.pathname] || 'this section';

  const isMaster = isMasterUser(currentUser);
  const userRoleCode = getUserRoleCode(currentUser);
  const canViewAreas = canViewInTopBar(isMaster, userRoleCode, '/areas');
  const canViewPatterns = canViewInTopBar(isMaster, userRoleCode, '/patterns');
  const canViewMeasurementSetup = canViewInTopBar(isMaster, userRoleCode, '/measurements');
  const canViewMeasurementList = canViewInTopBar(isMaster, userRoleCode, '/measurement-list');
  const canViewReports = canViewInTopBar(isMaster, userRoleCode, '/reports');
  const canViewDashboard = canViewInTopBar(isMaster, userRoleCode, '/dashboard');

  const availableSectionLabels = useMemo(() => {
    const labels: string[] = [];
    if (canViewDashboard) labels.push('Dashboard');
    if (canViewReports) labels.push('Reports');
    if (canViewMeasurementList) labels.push('Measurement list');
    if (canViewAreas) labels.push('Areas');
    if (canViewPatterns) labels.push('Patterns');
    if (canViewMeasurementSetup) labels.push('Measurement setup');
    if (isMaster) labels.push('Back Office');
    return labels;
  }, [
    canViewDashboard,
    canViewReports,
    canViewMeasurementList,
    canViewAreas,
    canViewPatterns,
    canViewMeasurementSetup,
    isMaster,
  ]);

  const openTourForCurrentRoute = React.useCallback((mode: 'auto' | 'manual' = 'manual') => {
    setTourMode(mode);
    setTourRouteKey(currentRouteKey);
    setTourOpen(false);
    if (isMobile) {
      setMobileNavOpen(false);
    }
  }, [currentRouteKey, isMobile]);

  const menuItems = useMemo(() => {
    if (isBackOfficeRoute) {
      return [
        {
          key: '/back-office?tab=subscriptions',
          icon: <CreditCardOutlined />,
          label: <NavLink to="/back-office?tab=subscriptions">Subscriptions</NavLink>,
        },
        {
          key: '/back-office?tab=boundaries',
          icon: <SettingOutlined />,
          label: <NavLink to="/back-office?tab=boundaries">Operating Boundaries</NavLink>,
        },
        {
          key: '/back-office?tab=devices',
          icon: <DeploymentUnitOutlined />,
          label: <NavLink to="/back-office?tab=devices">Devices</NavLink>,
        },
        {
          key: '/back-office?tab=modules',
          icon: <ExperimentOutlined />,
          label: <NavLink to="/back-office?tab=modules">Device Modules and Capabilities</NavLink>,
        },
        {
          key: '/back-office?tab=area-types',
          icon: <AppstoreOutlined />,
          label: <NavLink to="/back-office?tab=area-types">Area Types</NavLink>,
        },
        {
          key: '/back-office?tab=ranges',
          icon: <SlidersOutlined />,
          label: <NavLink to="/back-office?tab=ranges">Ranges</NavLink>,
        },
        {
          key: '/back-office?tab=report',
          icon: <SettingOutlined />,
          label: <NavLink to="/back-office?tab=report">Report</NavLink>,
        },
        {
          key: '/back-office?tab=agents',
          icon: <RobotOutlined />,
          label: <NavLink to="/back-office?tab=agents">Agents</NavLink>,
        },
      ];
    }

    const iconMap: Record<string, React.ReactNode> = {
      '/dashboard': <DashboardOutlined />,
      '/assistant': <RobotOutlined />,
      '/areas': <AppstoreOutlined />,
      '/patterns': <DeploymentUnitOutlined />,
      '/measurements': <FundProjectionScreenOutlined />,
      '/reports': <FileTextOutlined />,
      '/saved-reports': <FileTextOutlined />,
      '/measurement-list': <UnorderedListOutlined />,
      '/devices': <DeploymentUnitOutlined />,
      '/operations': <PlayCircleOutlined />,
      '/configuration': <SettingOutlined />,
      '/report-templates/settings': <SettingOutlined />,
      '/report-templates/wizard': <FileTextOutlined />,
    };

    const allowedItems = TOP_BAR_ITEM_AND_RESTRICTIONS
      .filter((item) => {
        if (item.isMasterOnly) {
          return isMaster;
        }
        if (item.roles && item.roles.length > 0) {
          if (isMaster) return true;
          return userRoleCode !== undefined && item.roles.includes(userRoleCode);
        }
        return true;
      });

    const itemByPath = new Map(allowedItems.map((item) => [item.path, item]));
    const makeMenuItem = (item: { path: string; label: string }) => ({
      key: item.path,
      icon: iconMap[item.path] || <AppstoreOutlined />,
      label: <NavLink to={item.path}>{item.label}</NavLink>,
    });

    const mainOrder = [
      '/dashboard',
      '/reports',
      '/saved-reports',
      '/measurement-list',
      '/devices',
    ];
    const robotControlOrder = ['/operations', '/slam-maps'];
    const setupOrder = ['/areas', '/patterns', '/measurements'];

    const menu: any[] = [];
    mainOrder.forEach((path) => {
      const item = itemByPath.get(path);
      if (item) menu.push(makeMenuItem(item));
    });

    const robotControlItems = robotControlOrder
      .map((path) => itemByPath.get(path))
      .filter(Boolean)
      .map((item) => makeMenuItem(item as any));

    if (selectedCompanyHasVehicle && robotControlItems.length > 0) {
      menu.push({ type: 'divider' });
      menu.push({
        type: 'group',
        key: 'robot-control-group',
        label: 'Robotic control',
        children: robotControlItems,
      });
    }

    const setupItems = setupOrder
      .map((path) => itemByPath.get(path))
      .filter(Boolean)
      .map((item) => makeMenuItem(item as any));

    if (setupItems.length > 0) {
      menu.push({ type: 'divider' });
      menu.push({
        type: 'group',
        key: 'setup-group',
        label: 'Setup',
        children: setupItems,
      });
      menu.push({ type: 'divider' });
    }

    const usedPaths = new Set([...mainOrder, ...robotControlOrder, ...setupOrder]);
    allowedItems.forEach((item) => {
      if (!usedPaths.has(item.path)) {
        menu.push(makeMenuItem(item));
      }
    });

    return menu;
  }, [isMaster, userRoleCode, isBackOfficeRoute, selectedCompanyHasVehicle]);

  const canViewSettings = useMemo(() => {
    if (isBackOfficeRoute) {
      return false;
    }
    return canViewInTopBar(isMaster, userRoleCode, '/configuration');
  }, [isMaster, userRoleCode, isBackOfficeRoute]);

  const companyName = useMemo(() => {
    const currentId = currentUser?.currentCompanyId || currentUser?.companyId;
    const associated = currentUser?.associatedCompanies || [];
    if (currentId) {
      const match = associated.find((c) => c.id === currentId);
      if (match?.name) return match.name;
    }
    return associated[0]?.name || 'Company';
  }, [currentUser]);

  const companyLogoUrl = useMemo(() => {
    const currentId = currentUser?.currentCompanyId || currentUser?.companyId;
    const membershipLogo = currentUser?.companyMemberships?.find(
      (membership) => membership.company?.id === currentId
    )?.company?.logoUrl;
    if (membershipLogo) return membershipLogo;
    const associated = currentUser?.associatedCompanies || [];
    if (currentId) {
      const match = associated.find((c) => c.id === currentId);
      if (match?.logoUrl) return match.logoUrl;
    }
    return associated[0]?.logoUrl || null;
  }, [currentUser]);

  const currentMembership = useMemo(() => {
    const currentId = currentUser?.currentCompanyId || currentUser?.companyId;
    if (!currentId) return null;
    return currentUser?.companyMemberships?.find((membership) => membership.company?.id === currentId) || null;
  }, [currentUser]);

  const currentTrialRemainingDays = typeof currentMembership?.trialRemainingDays === 'number'
    ? currentMembership.trialRemainingDays
    : null;
  const showTrialBanner = currentMembership?.license?.status === 'trial' && currentTrialRemainingDays !== null;
  const floatingHelpTopOffset = isMobile
    ? (showTrialBanner ? 96 : 58)
    : (showTrialBanner ? 66 : 16);
  const floatingHelpRightOffset = isMobile ? 12 : 18;

  const markRouteSeen = React.useCallback((routeKey: string) => {
    if (!onboardingStorageKey || !routeKey) return;
    mergeOnboardingState(onboardingStorageKey, (current) => ({
      ...current,
      seenRoutes: current.seenRoutes.includes(routeKey)
        ? current.seenRoutes
        : [...current.seenRoutes, routeKey],
    }));
  }, [onboardingStorageKey]);

  const mergeStepIds = React.useCallback((existing: string[], incoming: string[]) => {
    const merged = new Set<string>(existing || []);
    incoming.forEach((stepId) => {
      if (stepId) merged.add(stepId);
    });
    return Array.from(merged);
  }, []);

  const markLoadedStepIds = React.useCallback((stepIds: string[]) => {
    if (!onboardingStorageKey || stepIds.length === 0) return;
    mergeOnboardingState(onboardingStorageKey, (current) => ({
      ...current,
      loadedStepIds: mergeStepIds(current.loadedStepIds || [], stepIds),
    }));
  }, [onboardingStorageKey, mergeStepIds]);

  const markShownStepIds = React.useCallback((stepIds: string[]) => {
    if (!onboardingStorageKey || stepIds.length === 0) return;
    mergeOnboardingState(onboardingStorageKey, (current) => ({
      ...current,
      shownStepIds: mergeStepIds(current.shownStepIds || [], stepIds),
    }));
  }, [onboardingStorageKey, mergeStepIds]);

  const markCompletedStepIds = React.useCallback((stepIds: string[]) => {
    if (!onboardingStorageKey || stepIds.length === 0) return;
    mergeOnboardingState(onboardingStorageKey, (current) => {
      return {
        ...current,
        completedStepIds: mergeStepIds(current.completedStepIds || current.seenStepIds || [], stepIds),
        // Keep legacy field in sync for backward compatibility with old clients/state.
        seenStepIds: mergeStepIds(current.seenStepIds || [], stepIds),
      };
    });
  }, [onboardingStorageKey, mergeStepIds]);

  const completeOnboardingWizard = React.useCallback(() => {
    if (!onboardingStorageKey) return;
    mergeOnboardingState(onboardingStorageKey, (current) => ({
      ...current,
      wizardCompleted: true,
    }));
  }, [onboardingStorageKey]);

  const onboardingSteps = useMemo<OnboardingStepItem[]>(() => {
    const steps: OnboardingStepItem[] = [
      {
        title: 'Welcome to OSCAR',
        description: 'This quick setup will help you configure your workspace and show where key tools are.',
      },
    ];

    if (canViewSettings) {
      steps.push({
        title: 'Set company profile and logo',
        description: 'Open Configuration and complete branding details so reports and pages use your company identity.',
        actionLabel: 'Open Configuration',
        onAction: () => navigate('/configuration?tab=company'),
      });
      steps.push({
        title: 'Link communication channel',
        description: 'In Profile, link your WhatsApp number and personal account settings for conversational workflows.',
        actionLabel: 'Open Profile',
        onAction: () => navigate('/configuration?tab=profile'),
      });
    }

    if (isMaster) {
      steps.push({
        title: 'Define limits and custom settings',
        description: 'As Master, use Back Office to manage subscriptions, area types, report settings, and agents.',
        actionLabel: 'Open Back Office',
        onAction: () => navigate('/back-office?tab=subscriptions'),
      });
    } else if (canViewSettings) {
      steps.push({
        title: 'Check subscription and limits',
        description: 'As Admin, review active limits in Configuration > Subscription for your current company.',
        actionLabel: 'Open Subscription',
        onAction: () => navigate('/configuration?tab=subscription'),
      });
    }

    if (availableSectionLabels.length > 0) {
      steps.push({
        title: 'Sections available to your role',
        description: (
          <div>
            <Text strong>Accessible sections:</Text>
            <div className="layout-onboarding-list">{availableSectionLabels.join(' · ')}</div>
          </div>
        ),
      });
    }

    steps.push({
      title: 'Page tours enabled',
      description: 'Guided page tours are available from the sidebar whenever you want them. Use "Replay tour" for step-by-step help in the current section.',
    });

    return steps;
  }, [canViewSettings, isMaster, navigate, availableSectionLabels]);

  const onboardingLastStepIndex = onboardingSteps.length - 1;
  const onboardingCurrentStep = onboardingSteps[onboardingStepIndex] || onboardingSteps[0];

  React.useEffect(() => {
    if (!onboardingStorageKey || !currentUser) return;
    const state = readOnboardingState(onboardingStorageKey);
    if (!state.wizardCompleted) {
      setOnboardingOpen(true);
      setOnboardingStepIndex(0);
    }
  }, [onboardingStorageKey, currentUser]);

  const buildRouteSpecificTourDefinitions = React.useCallback((routeKey: string): TourStepDefinition[] => {
    switch (routeKey) {
      case '/dashboard':
        return [
          {
            id: 'dashboard.insights',
            title: 'Warning notifications',
            description: 'These shows what areas are outside the predefined range in settings.',
            selector: '.dashboard-insights-card',
          },
          {
            id: 'dashboard.latest-measurement',
            title: 'Latest measurement',
            description: 'This card shows the most recent measurement summary and quick actions to drill into details.',
            selector: '.dashboard-latest-measurement-section',
          },
          {
            id: 'dashboard.favorite-area',
            title: 'Favorite area',
            description: 'This section highlights your configured favorite area so you can jump to it quickly.',
            selector: '.dashboard-favorite-section',
          },
          
          {
            id: 'dashboard.grid',
            title: 'Insights List',
            description: 'This list show area trends and highlights where to investigate first.',
            selector: '.dashboard-insights-grid',
          },
        ];
      case '/reports':
        return [

          {
            id: 'reports.search-areas',
            title: 'Search areas',
            description: 'Use this search box to quickly find the area you want to review.',
            selector: '.reports-filter-input',
          },
          {
            id: 'reports.status-sections',
            title: 'Action needed, attention, healthy, and no data',
            description: 'These status sections group areas by latest health condition so you can prioritize work.',
            selector: '.reports-status-cards',
          },
          {
            id: 'reports.table',
            title: 'Report list',
            description: 'Each row is an area report entry; open one to inspect analysis details.',
            selector: '.reports-table',
          },
          {
            id: 'reports.health-overview',
            title: 'Health overview (selected area)',
            description: 'This overview tracks the selected area and shows the current status ratios by health category.',
            selector: '.reports-overview-card',
          },
          {
            id: 'reports.map',
            title: 'Area map',
            description: 'The map outlines the selected area and colors areas by status for quick comparison.',
            selector: '.reports-map-card',
          },
          {
            id: 'reports.latest-data',
            title: 'Latest data by area',
            description: 'This section lists different areas with their latest session and lets you open the most recent report directly.',
            selector: '.reports-latest-card',
          },
          {
            id: 'reports.visualizations.container',
            title: 'Generated report visualizations',
            description: 'After selecting area/template/date, report visualization components are rendered in this panel.',
            selector: '.reports-detail-card',
          },
          {
            id: 'reports.visualizations.kpi-cards',
            title: 'KPI cards',
            description: 'KPI cards summarize the most important metrics at a glance.',
            selector: '.reports-detail-card .report-section-visual-kpi-cards',
          },
          {
            id: 'reports.visualizations.table',
            title: 'Table',
            description: 'Table visualization shows detailed metric rows and values.',
            selector: '.reports-detail-card .report-section-visual-table',
          },
          {
            id: 'reports.visualizations.line-chart',
            title: 'Line chart',
            description: 'Line chart visualizations show trend evolution across time.',
            selector: '.reports-detail-card .report-section-visual-line-chart',
          },
          {
            id: 'reports.visualizations.bar-chart',
            title: 'Bar chart',
            description: 'Bar chart visualizations compare values across entities or periods.',
            selector: '.reports-detail-card .report-section-visual-bar-chart',
          },
          {
            id: 'reports.visualizations.pattern-point-line',
            title: 'Pattern point line chart',
            description: 'Shows metric progression by pattern point index across measurements.',
            selector: '.reports-detail-card .report-section-visual-pattern-point-line-chart',
          },
          {
            id: 'reports.visualizations.heatmap',
            title: 'Heatmap',
            description: 'Heatmap visualizations show spatial intensity of selected metrics on the area.',
            selector: '.reports-detail-card .report-section-visual-heatmap',
          },
          {
            id: 'reports.visualizations.animated-heatmap',
            title: 'Animated heatmap',
            description: 'Animated heatmaps show how spatial patterns evolve over time.',
            selector: '.reports-detail-card .report-section-visual-animated-heatmap',
          },
          {
            id: 'reports.visualizations.animated-pattern-points',
            title: 'Animated pattern points',
            description: 'Shows time-based playback on pattern points.',
            selector: '.reports-detail-card .report-section-visual-animated-pattern-points',
          },
          {
            id: 'reports.visualizations.area-hierarchy',
            title: 'Area hierarchy',
            description: 'Area hierarchy visualizations show parent-child relationships and statuses.',
            selector: '.reports-detail-card .report-section-visual-area-hierarchy',
          },
          {
            id: 'reports.visualizations.pattern-list',
            title: 'Pattern list',
            description: 'Pattern list visualizations show pattern usage and point references.',
            selector: '.reports-detail-card .report-section-visual-pattern-list',
          },
          {
            id: 'reports.visualizations.availability-calendar',
            title: 'Availability calendar',
            description: 'Calendar visualizations show data availability by day.',
            selector: '.reports-detail-card .report-section-visual-availability-calendar',
          },
          {
            id: 'reports.visualizations.measurement-list',
            title: 'Measurement list',
            description: 'Lists raw measurements included in the current report context.',
            selector: '.reports-detail-card .report-section-visual-measurement-list',
          },
          {
            id: 'reports.visualizations.measurement-list-links',
            title: 'View report links',
            description: 'Measurement code links are clickable and open that measurement report context directly.',
            selector: '.reports-detail-card .report-section-visual-measurement-list .report-measurement-list-report-link',
          },
          {
            id: 'reports.visualizations.measurement-list-points',
            title: 'Clickable pattern points',
            description: 'Pattern point entries are clickable so you can jump to point-level report analysis.',
            selector: '.reports-detail-card .report-section-visual-measurement-list .report-measurement-list-point-link',
          },
          {
            id: 'reports.visualizations.measurement-list-downloads',
            title: 'Download buttons',
            description: 'Use these buttons to export selected measurements as CSV or files ZIP.',
            selector: '.reports-detail-card .report-section-visual-measurement-list .report-measurement-list-actions',
          },
          {
            id: 'reports.visualizations.measurement-list-ticks',
            title: 'Tickboxes',
            description: 'Use header and row tickboxes to select measurements before downloading.',
            selector: '.reports-detail-card .report-section-visual-measurement-list .report-measurement-list-table .ant-table-selection-column',
          },
          {
            id: 'reports.visualizations.measurement-info',
            title: 'Measurement info',
            description: 'Shows primary metadata for a selected measurement.',
            selector: '.reports-detail-card .report-section-visual-measurement-info',
          },
          {
            id: 'reports.visualizations.measurement-values',
            title: 'Measurement values',
            description: 'Displays extracted numeric/text values from selected measurements.',
            selector: '.reports-detail-card .report-section-visual-measurement-values',
          },
          {
            id: 'reports.visualizations.measurement-texts',
            title: 'Measurement notes',
            description: 'Displays notes, comments, and qualitative logs.',
            selector: '.reports-detail-card .report-section-visual-measurement-texts',
          },
          {
            id: 'reports.visualizations.measurement-weather',
            title: 'Measurement weather',
            description: 'Shows weather conditions attached to measurements.',
            selector: '.reports-detail-card .report-section-visual-measurement-weather',
          },
          {
            id: 'reports.visualizations.measurement-location',
            title: 'Measurement location',
            description: 'Location map for measurement coordinates.',
            selector: '.reports-detail-card .report-section-visual-measurement-location',
          },
          {
            id: 'reports.visualizations.measurement-pattern-location',
            title: 'Measurement pattern location',
            description: 'Shows measurement position relative to pattern points.',
            selector: '.reports-detail-card .report-section-visual-measurement-pattern-location',
          },
          {
            id: 'reports.visualizations.measurement-files',
            title: 'Measurement files',
            description: 'Shows linked images/files for the selected measurements.',
            selector: '.reports-detail-card .report-section-visual-measurement-files',
          },
          {
            id: 'reports.visualizations.latest-analysis',
            title: 'Latest analysis',
            description: 'Summarizes latest insights and recommended actions.',
            selector: '.reports-detail-card .report-section-visual-latest-analysis',
          },
          {
            id: 'reports.visualizations.warning-card',
            title: 'Warning card',
            description: 'Highlights high-priority anomalies and alerts.',
            selector: '.reports-detail-card .report-section-visual-warning-card',
          },
        ];
      case '/saved-reports':
        return [
          {
            id: 'saved-reports.generate',
            title: 'Generate report',
            description: 'Use this button to create a new report run from the reports workflow.',
            selector: '.saved-reports-generate-button',
          },
          {
            id: 'saved-reports.table',
            title: 'Saved report snapshots',
            description: 'This table lists saved reports with area, template, status, and date range metadata.',
            selector: '.saved-reports-table',
          },
          {
            id: 'saved-reports.title-link',
            title: 'Open saved report',
            description: 'Click a report title to open that saved report view.',
            selector: '.saved-reports-title-link',
          },
          {
            id: 'saved-reports.sharing',
            title: 'Sharing',
            description: 'Use sharing to grant access to users inside your company and invite external emails.',
            selector: '.saved-reports-sharing-manage',
          },
        ];
      case '/saved-reports/:id':
        return [
          {
            id: 'saved-report-view.container',
            title: 'Report container',
            description: 'All visualization sections for this saved snapshot are rendered here.',
            selector: '.saved-report-view-report-card',
          },
          {
            id: 'saved-report-view.sections',
            title: 'Visualization sections',
            description: 'Each section card is one visualization component from the report template.',
            selector: '.report-section-card',
          },
          {
            id: 'saved-report-view.kpi-cards',
            title: 'KPI cards',
            description: 'KPI cards summarize core metrics with quick high-level values.',
            selector: '.report-section-visual-kpi-cards',
          },
          {
            id: 'saved-report-view.table',
            title: 'Table',
            description: 'Table sections show detailed tabular values and comparisons.',
            selector: '.report-section-visual-table',
          },
          {
            id: 'saved-report-view.line-chart',
            title: 'Line chart',
            description: 'Line charts show metric trends across time or grouped entities.',
            selector: '.report-section-visual-line-chart',
          },
          {
            id: 'saved-report-view.bar-chart',
            title: 'Bar chart',
            description: 'Bar charts compare metrics across areas, sessions, or categories.',
            selector: '.report-section-visual-bar-chart',
          },
          {
            id: 'saved-report-view.pattern-point-line',
            title: 'Pattern point line chart',
            description: 'Pattern point lines compare values by point index across sessions.',
            selector: '.report-section-visual-pattern-point-line-chart',
          },
          {
            id: 'saved-report-view.heatmap',
            title: 'Heatmap',
            description: 'Heatmaps visualize metric intensity across the area geometry.',
            selector: '.report-section-visual-heatmap',
          },
          {
            id: 'saved-report-view.animated-heatmap',
            title: 'Animated heatmap',
            description: 'Animated heatmaps show how spatial distribution changes over time.',
            selector: '.report-section-visual-animated-heatmap',
          },
          {
            id: 'saved-report-view.animated-pattern-points',
            title: 'Animated pattern points',
            description: 'Animated point playback shows metric changes on pattern points over time.',
            selector: '.report-section-visual-animated-pattern-points',
          },
          {
            id: 'saved-report-view.area-hierarchy',
            title: 'Area hierarchy',
            description: 'Hierarchy sections show parent-child area structure and linked health context.',
            selector: '.report-section-visual-area-hierarchy',
          },
          {
            id: 'saved-report-view.pattern-list',
            title: 'Pattern list',
            description: 'Pattern list sections show pattern definitions, usage, and mapped points.',
            selector: '.report-section-visual-pattern-list',
          },
          {
            id: 'saved-report-view.availability-calendar',
            title: 'Availability calendar',
            description: 'Calendar sections show data coverage and session availability by day.',
            selector: '.report-section-visual-availability-calendar',
          },
          {
            id: 'saved-report-view.measurement-list',
            title: 'Measurement list',
            description: 'Measurement list sections provide raw sessions and measurement-level context.',
            selector: '.report-section-visual-measurement-list',
          },
          {
            id: 'saved-report-view.measurement-list-links',
            title: 'View report links',
            description: 'Measurement code links are clickable and open that measurement report context directly.',
            selector: '.report-section-visual-measurement-list .report-measurement-list-report-link',
          },
          {
            id: 'saved-report-view.measurement-list-points',
            title: 'Clickable pattern points',
            description: 'Pattern point entries are clickable so you can jump to point-level report analysis.',
            selector: '.report-section-visual-measurement-list .report-measurement-list-point-link',
          },
          {
            id: 'saved-report-view.measurement-list-downloads',
            title: 'Download buttons',
            description: 'Use these buttons to export selected measurements as CSV or files ZIP.',
            selector: '.report-section-visual-measurement-list .report-measurement-list-actions',
          },
          {
            id: 'saved-report-view.measurement-list-ticks',
            title: 'Tickboxes',
            description: 'Use header and row tickboxes to select measurements before downloading.',
            selector: '.report-section-visual-measurement-list .report-measurement-list-table .ant-table-selection-column',
          },
          {
            id: 'saved-report-view.measurement-info',
            title: 'Measurement info',
            description: 'Measurement info shows core metadata for the selected measurement.',
            selector: '.report-section-visual-measurement-info',
          },
          {
            id: 'saved-report-view.measurement-values',
            title: 'Measurement values',
            description: 'Measurement values show extracted numeric/text values for the selected reading.',
            selector: '.report-section-visual-measurement-values',
          },
          {
            id: 'saved-report-view.measurement-texts',
            title: 'Measurement notes',
            description: 'Measurement texts show notes, comments, and qualitative observations.',
            selector: '.report-section-visual-measurement-texts',
          },
          {
            id: 'saved-report-view.measurement-weather',
            title: 'Measurement weather',
            description: 'Weather sections show environmental conditions captured with measurements.',
            selector: '.report-section-visual-measurement-weather',
          },
          {
            id: 'saved-report-view.measurement-location',
            title: 'Measurement location',
            description: 'Location map sections show where the measurement was captured.',
            selector: '.report-section-visual-measurement-location',
          },
          {
            id: 'saved-report-view.measurement-pattern-location',
            title: 'Measurement pattern location',
            description: 'Pattern location sections show measurement position relative to pattern points.',
            selector: '.report-section-visual-measurement-pattern-location',
          },
          {
            id: 'saved-report-view.measurement-files',
            title: 'Measurement files',
            description: 'Files sections list linked images and uploaded evidence.',
            selector: '.report-section-visual-measurement-files',
          },
          {
            id: 'saved-report-view.latest-analysis',
            title: 'Latest analysis',
            description: 'Latest analysis summarizes current findings and suggested actions.',
            selector: '.report-section-visual-latest-analysis',
          },
          {
            id: 'saved-report-view.warning-card',
            title: 'Warning card',
            description: 'Warning cards highlight key risk alerts and attention points.',
            selector: '.report-section-visual-warning-card',
          },
        ];
      case '/areas':
        return [
          {
            id: 'areas.new-area',
            title: 'Create new parent area',
            description: 'Use this button to create a new top-level parent area.',
            selector: '.areas-new-button',
            placement: 'bottom',
          },
          {
            id: 'areas.add-sub-area',
            title: 'Add sub-area',
            description: 'Use Add sub-area to create child areas under the selected parent area.',
            selector: '.area-add-button',
          },
          {
            id: 'areas.edit',
            title: 'Edit area',
            description: 'Edit updates area details and geometry for the selected row.',
            selector: '.area-edit-button',
          },
          {
            id: 'areas.delete',
            title: 'Delete area',
            description: 'Delete removes an area when it has no child dependencies.',
            selector: '.area-delete-button',
          },
          {
            id: 'areas.table',
            title: 'Areas table',
            description: 'This table is the source of truth for area names, parent-child links, and edits.',
            selector: '.areas-table',
          },
          {
            id: 'areas.map',
            title: 'Area map',
            description: 'The map validates geometry and helps verify boundaries.',
            selector: '.areas-map-card',
          },
        ];
      case '/areas/new':
        return [
          {
            id: 'areas.create.type',
            title: 'Area type',
            description: 'Select the area type first. If the selected type has a rectangular template, the shape is added on the map automatically.',
            selector: '.area-create-type-field',
          },
          {
            id: 'areas.create.parent',
            title: 'Parent area',
            description: 'Use Parent area to place this new area under an existing hierarchy node.',
            selector: '.area-create-parent-field',
          },
          {
            id: 'areas.create.name-code',
            title: 'Name and code',
            description: 'Enter the area name. The code is auto-generated from name + type and can still be adjusted before saving.',
            selector: '.area-create-name-field',
          },
          {
            id: 'areas.create.map',
            title: 'Map',
            description: 'Draw and validate the area boundary directly on this map.',
            selector: '.area-create-map-card',
          },
          {
            id: 'areas.create.search',
            title: 'Search bar',
            description: 'Use search to jump the map to the location where you want to create the area.',
            selector: '.area-create-search-input',
          },
          {
            id: 'areas.create.polygon-button',
            title: 'Polygon creation',
            description: 'Use this polygon tool to draw a new shape on the map.',
            selector: '.area-create-draw-polygon-button, .mapbox-gl-draw_polygon',
          },
          {
            id: 'areas.create.trash-button',
            title: 'Trash tool',
            description: 'Use the trash tool to clear the current shape and redraw.',
            selector: '.area-create-draw-trash-button, .mapbox-gl-draw_trash, .mapbox-gl-draw_ctrl-icon-trash',
          },
          {
            id: 'areas.create.save-cancel',
            title: 'Save and cancel',
            description: 'Cancel returns to the areas list without saving. Save creates the area with the current details and geometry.',
            selector: '.area-create-actions',
          },
        ];
      case '/areas/:id/edit':
        return [
          {
            id: 'areas.edit.type',
            title: 'Area type',
            description: 'Review or update the area type for this existing area.',
            selector: '.area-create-type-field',
          },
          {
            id: 'areas.edit.parent',
            title: 'Parent area',
            description: 'Use Parent area to move this area under another hierarchy node.',
            selector: '.area-create-parent-field',
          },
          {
            id: 'areas.edit.name-code',
            title: 'Name and code',
            description: 'Update name and code as needed before saving changes.',
            selector: '.area-create-name-field',
          },
          {
            id: 'areas.edit.map',
            title: 'Map',
            description: 'Adjust the existing geometry directly on the map.',
            selector: '.area-create-map-card',
          },
          {
            id: 'areas.edit.search',
            title: 'Search bar',
            description: 'Use search to move map focus faster while editing geometry.',
            selector: '.area-create-search-input',
          },
          {
            id: 'areas.edit.polygon-button',
            title: 'Polygon creation',
            description: 'Use this tool if you need to redraw the area geometry.',
            selector: '.area-create-draw-polygon-button, .mapbox-gl-draw_polygon',
          },
          {
            id: 'areas.edit.trash-button',
            title: 'Trash tool',
            description: 'Use the trash tool to clear geometry before redrawing.',
            selector: '.area-create-draw-trash-button, .mapbox-gl-draw_trash, .mapbox-gl-draw_ctrl-icon-trash',
          },
          {
            id: 'areas.edit.save-cancel',
            title: 'Save and cancel',
            description: 'Cancel discards edits. Update saves the modified details and geometry.',
            selector: '.area-create-actions',
          },
        ];
      case '/patterns':
      case '/patterns?mode=template':
      case '/patterns?mode=gps':
        return [
          {
            id: 'patterns.area-type',
            title: 'Area type filter',
            description: 'Select the area type first. 4-corner types are template-based; types with more than 4 points are GPS-based per-area.',
            selector: '.patterns-area-type-control',
          },
          {
            id: 'patterns.mode.template',
            title: 'Template mode (4 corners)',
            description: 'For 4-corner area types, pattern creation uses the template image and normalized coordinates.',
            selector: '.patterns-mode-template .patterns-preview-panel',
          },
          {
            id: 'patterns.mode.gps',
            title: 'GPS mode (more than 4 points)',
            description: 'For non-rectangular area types, patterns are created per selected area using GPS coordinates.',
            selector: '.patterns-mode-gps .patterns-fallback-areas-panel',
          },
          {
            id: 'patterns.areas',
            title: 'Areas list',
            description: 'This list is populated when the selected area type is non-rectangular (more than 4 corners). Any pattern you create here applies only to the selected area.',
            selector: '.patterns-fallback-areas-panel',
          },
          {
            id: 'patterns.list',
            title: 'Patterns list',
            description: 'Browse available patterns, select one, and use actions like edit or delete.',
            selector: '.patterns-list-panel, .patterns-fallback-patterns-panel',
          },
          {
            id: 'patterns.create',
            title: 'Create new pattern',
            description: 'Use this action to start pattern creation with the current context. In template mode it creates a pattern for the selected area type; in non-rectangular mode it creates a pattern only for the selected area.',
            selector: '.patterns-create-button',
          },
          {
            id: 'patterns.preview',
            title: 'Preview or map',
            description: 'You can validate point placement in template preview mode or map mode.',
            selector: '.patterns-preview-panel, .patterns-fallback-map',
          },
          {
            id: 'patterns.points',
            title: 'Pattern points',
            description: 'Point markers show the point order and exact placement that measurements will use.',
            selector: '.patterns-preview-canvas, .patterns-fallback-map',
          },
        ];
      case '/patterns/create/template':
        return [

          {
            id: 'patterns.create-template.metadata',
            title: 'Pattern details',
            description: 'Set the pattern name and type before saving.',
            selector: '.pattern-create-metadata',
          },
          {
            id: 'patterns.create-template.mode',
            title: 'Tool mode',
            description: 'Choose Manual to place points one by one, or Grid to auto-generate points.',
            selector: '.pattern-create-tool-mode',
          },
          {
            id: 'patterns.create-template.grid',
            title: 'Grid configuration',
            description: 'In Grid mode, configure rows/columns, offsets, and ordering before generating points.',
            selector: '.pattern-create-grid-config',
          },
          {
            id: 'patterns.create-template.canvas',
            title: 'Template canvas',
            description: 'Place and move points directly on the template image.',
            selector: '.pattern-create-template-canvas',
          },
          {
            id: 'patterns.create-template.points-table',
            title: 'Points table',
            description: 'Fine-tune coordinates and labels for each generated point.',
            selector: '.pattern-create-points-table',
          },
          {
            id: 'patterns.create-template.actions',
            title: 'Save and cancel',
            description: 'Save stores the pattern points for this area type. Cancel returns without saving.',
            selector: '.pattern-create-actions',
          },
        ];
      case '/patterns/create/area':
        return [

          {
            id: 'patterns.create-area.metadata',
            title: 'Pattern details',
            description: 'Set pattern name and type for the selected area.',
            selector: '.pattern-create-metadata',
          },
          {
            id: 'patterns.create-area.mode',
            title: 'Tool mode',
            description: 'Choose Manual for click-based point placement or Grid for automated point generation.',
            selector: '.pattern-create-tool-mode',
          },
          {
            id: 'patterns.create-area.grid',
            title: 'Grid configuration',
            description: 'Configure spacing, rotation, edge clearance, and pattern order for GPS grid generation.',
            selector: '.pattern-create-grid-config',
          },
          {
            id: 'patterns.create-area.map',
            title: 'Interactive map',
            description: 'The area boundary and point markers are shown here. Add or preview points directly on map.',
            selector: '.pattern-create-area-map',
          },
          {
            id: 'patterns.create-area.points-table',
            title: 'Coordinate table',
            description: 'Review and adjust latitude/longitude and optional labels per point.',
            selector: '.pattern-create-points-table',
          },
          {
            id: 'patterns.create-area.actions',
            title: 'Save and cancel',
            description: 'Save stores the pattern for the selected area. Cancel returns without saving.',
            selector: '.pattern-create-actions',
          },
        ];
      case '/measurements':
        return [
          {
            id: 'measurements.date-filter',
            title: 'Date range filter',
            description: 'Set the date range first. It controls which area counts and measurements are shown.',
            selector: '.measurements-date-filter-card',
          },
          {
            id: 'measurements.unassigned',
            title: 'Unassigned measurements',
            description: 'If present, this section shows measurements with no area assigned so you can prioritize assignment.',
            selector: '.measurements-unassigned-card, .measurements-areas-table-card',
          },
          {
            id: 'measurements.areas-table',
            title: 'Areas with measurements',
            description: 'Select an area row to load measurements and map points for that area context.',
            selector: '.measurements-areas-table-card',
          },
          {
            id: 'measurements.list',
            title: 'Measurement assignment list',
            description: 'Measurements are grouped by pattern and point status. Use row actions to assign, edit, or unassign.',
            selector: '.measurements-list-card',
          },
          {
            id: 'measurements.action.view-details',
            title: 'View details',
            description: 'Use View Details to open the report context for that measurement and investigate analysis output.',
            selector: '.measurement-action-view-details',
          },
          {
            id: 'measurements.action.gps',
            title: 'Set / edit GPS',
            description: 'Use this action to set GPS when missing or edit GPS coordinates when already present.',
            selector: '.measurement-action-edit-gps',
          },
          {
            id: 'measurements.action.assign-area',
            title: 'Assign to area',
            description: 'Use this action for unassigned measurements to open area assignment flow.',
            selector: '.measurement-action-assign-area',
          },
          {
            id: 'measurements.action.assign-pattern',
            title: 'Assign or edit pattern',
            description: 'Use Assign to pattern or Edit pattern to link measurements to a pattern and optionally to pattern points.',
            selector: '.measurement-action-assign-pattern, .measurement-action-edit-pattern',
          },
          {
            id: 'measurements.action.unassign',
            title: 'Unassign options',
            description: 'Use Unassign to remove area, pattern, or point links when you need to correct assignment.',
            selector: '.measurement-action-unassign',
          },
          {
            id: 'measurements.pagination',
            title: 'Pagination',
            description: 'Use pagination to navigate large result sets and adjust page size.',
            selector: '.measurements-pagination',
          },
          {
            id: 'measurements.map',
            title: 'Measurement map',
            description: 'Map context helps verify area boundaries, pattern points, and measurement GPS locations.',
            selector: '.measurements-map-card',
          },
          {
            id: 'measurements.details',
            title: 'Measurement details',
            description: 'Select any measurement to inspect values, notes, images, and location details.',
            selector: '.measurements-details-card',
          },
        ];
      case '/measurements/assign-areas':
        return [
          {
            id: 'measurements.assign-areas.date',
            title: 'Date range filter',
            description: 'Use date range to control which unassigned measurements are available.',
            selector: '.assign-areas-date-filter-card',
          },
          {
            id: 'measurements.assign-areas.target',
            title: 'Target area',
            description: 'Select the area where selected measurements should be assigned.',
            selector: '.assign-areas-target-card',
          },
          {
            id: 'measurements.assign-areas.list',
            title: 'Unassigned measurement list',
            description: 'Select one or many measurements here before assigning.',
            selector: '.assign-areas-table-card',
          },
          {
            id: 'measurements.assign-areas.map',
            title: 'Map preview',
            description: 'Verify measurement points against the selected area boundary before applying.',
            selector: '.assign-areas-map-card',
          },
          {
            id: 'measurements.assign-areas.apply',
            title: 'Apply assignment',
            description: 'Use Assign to area to apply area links to all selected measurements.',
            selector: '.assign-areas-assign-button',
          },
        ];
      case '/measurements/assign-patterns':
        return [
          {
            id: 'measurements.assign-patterns.workflow',
            title: 'Assignment workflow',
            description: 'Follow the sequence: area → pattern → mode → optional point selection.',
            selector: '.assign-patterns-workflow-card',
          },
          {
            id: 'measurements.assign-patterns.table',
            title: 'Area measurements',
            description: 'Select measurements to assign, including already assigned ones if you need to reassign.',
            selector: '.assign-patterns-table-card',
          },
          {
            id: 'measurements.assign-patterns.preview',
            title: 'Pattern preview',
            description: 'This template view shows the selected pattern points in template coordinates (x/y local) so you can choose the correct target point.',
            selector: '.assign-patterns-preview-card',
          },
          {
            id: 'measurements.assign-patterns.map',
            title: 'Area map (real GPS)',
            description: 'This map shows real GPS measurement points and pattern point locations on the selected area, so you can validate assignment against actual field positions.',
            selector: '.assign-patterns-map-card',
          },
          {
            id: 'measurements.assign-patterns.apply',
            title: 'Apply pattern assignment',
            description: 'Use Assign to pattern to update selected measurements with pattern and point linkage.',
            selector: '.assign-patterns-assign-button',
          },
        ];
      case '/measurements/:id/edit-location':
        return [
          {
            id: 'measurements.edit-gps.coords',
            title: 'Coordinate inputs',
            description: 'Latitude and longitude can be entered manually for precise updates.',
            selector: '.measurement-edit-coordinates-card',
          },
          {
            id: 'measurements.edit-gps.search',
            title: 'Location search',
            description: 'Search for a place and pick it to update coordinates quickly.',
            selector: '.measurement-edit-search',
          },
          {
            id: 'measurements.edit-gps.map',
            title: 'Interactive map',
            description: 'Click map or drag marker to adjust location visually.',
            selector: '.measurement-edit-map-card',
          },
          {
            id: 'measurements.edit-gps.save',
            title: 'Save location',
            description: 'Save writes the new GPS coordinates to the measurement record.',
            selector: '.measurement-edit-save-button',
          },
        ];
      case '/measurement-list':
        return [
          {
            id: 'measurement-list.date-range',
            title: 'Date range',
            description: 'Choose the date range to define which measurements are listed below.',
            selector: '.measurement-list-date-range',
          },
          {
            id: 'measurement-list.selection',
            title: 'Tickboxes',
            description: 'Use the header tickbox and row tickboxes to select measurements for bulk actions.',
            selector: '.measurement-list-table .ant-table-selection-column',
          },
          {
            id: 'measurement-list.download-buttons',
            title: 'Download buttons',
            description: 'Download selected rows as CSV or files ZIP once you tick the measurements you need.',
            selector: '.measurement-list-download-actions',
          },
          {
            id: 'measurement-list.links',
            title: 'Clickable links to reports',
            description: 'Area, pattern, pattern point, and measurement code links open the reports view for that context in the selected date range.',
            selector: '.measurement-list-report-link',
          },
          {
            id: 'measurement-list.measured-at',
            title: 'Measured At',
            description: 'This column shows when the measurement was captured.',
            selector: '.measurement-list-measured-at-col',
          },
          {
            id: 'measurement-list.created-at',
            title: 'Created At',
            description: 'This column shows when the record was created in the system.',
            selector: '.measurement-list-created-at-col',
          },
          {
            id: 'measurement-list.metrics',
            title: 'Measurement metrics',
            description: 'Metric columns appear after Measured At and Created At, showing processed values per measurement.',
            selector: '.measurement-list-metric-col',
          },
          {
            id: 'measurement-list.pagination',
            title: 'Pagination',
            description: 'Use pagination controls to move through pages and adjust page size.',
            selector: '.measurement-list-table .ant-pagination',
          },
          {
            id: 'measurement-list.table',
            title: 'Measurement table',
            description: 'This is the operational table for selecting, exporting, and opening report contexts.',
            selector: '.measurement-list-table',
          },
        ];
      case '/devices':
        return [
          {
            id: 'devices.stats.cards',
            title: 'Top 5 device cards',
            description: 'These five cards summarize total devices, online count, recent activity, total measurements, and location coverage.',
            selector: '.devices-stats-grid',
          },
          {
            id: 'devices.list',
            title: 'Devices list',
            description: 'This list shows all devices with status and usage details.',
            selector: '.devices-table-card',
          },
          {
            id: 'devices.list.clickable',
            title: 'Clickable device rows',
            description: 'Click any device row to update usage history and map context for that selected device.',
            selector: '.devices-table',
          },
          {
            id: 'devices.usage.filters',
            title: 'Usage filters',
            description: 'Use this range selector to switch the usage history window (30 days, month, year, or 5 years).',
            selector: '.devices-usage-range',
          },
          {
            id: 'devices.usage.plot',
            title: 'Usage history plot',
            description: 'This chart shows measurement count over time for the currently selected device.',
            selector: '.devices-usage-card',
          },
          {
            id: 'devices.map',
            title: 'Last known location',
            description: 'The map on the right shows the latest known location for devices and highlights your selected one.',
            selector: '.devices-map-card',
          },
          {
            id: 'devices.map.last-usage',
            title: 'Latest usage details',
            description: 'This footer shows the selected device with coordinates and latest heartbeat/usage context.',
            selector: '.devices-map-footer',
          },
        ];
      case '/assistant':
        return [
          {
            id: 'assistant.window',
            title: 'Assistant conversation',
            description: 'Chat history and analysis responses appear in this window.',
            selector: '.assistant-chat-window',
          },
          {
            id: 'assistant.suggestions',
            title: 'Suggested prompts',
            description: 'Use suggestions to start common workflows faster.',
            selector: '.assistant-suggestions',
          },
          {
            id: 'assistant.input',
            title: 'Assistant input',
            description: 'Send prompts, upload files, or add contextual inputs here.',
            selector: '.assistant-input-row',
          },
        ];
      case '/configuration':
      case '/configuration?tab=profile':
        return [

          {
            id: 'configuration.tabs',
            title: 'Configuration tabs',
            description: 'Switch between profile, users, company, and subscription settings.',
            selector: '.configuration-tabs',
          },
          {
            id: 'configuration.profile.identity',
            title: 'Profile details',
            description: 'This section shows your identity, role, and current company context.',
            selector: '.configuration-tab-profile .configuration-profile-identity',
          },
          {
            id: 'configuration.profile.dashboard',
            title: 'Dashboard settings',
            description: 'Choose favorite metrics and area for your dashboard defaults, then save.',
            selector: '.configuration-tab-profile .configuration-profile-dashboard-section',
          },
          {
            id: 'configuration.profile.whatsapp',
            title: 'WhatsApp linking',
            description: 'Generate QR, link your number, and manage linked WhatsApp numbers here.',
            selector: '.configuration-tab-profile .configuration-profile-whatsapp-section',
          },
          {
            id: 'configuration.profile.logout',
            title: 'Logout action',
            description: 'Use this button to sign out from your current session.',
            selector: '.configuration-tab-profile .configuration-profile-logout-button',
          },
        ];
      case '/configuration?tab=users':
        return [

          {
            id: 'configuration.tabs',
            title: 'Configuration tabs',
            description: 'Switch between profile, users, company, and subscription settings.',
            selector: '.configuration-tabs',
          },
          {
            id: 'configuration.users.card',
            title: 'User management',
            description: 'Manage company users and invitations from this panel.',
            selector: '.configuration-tab-users .configuration-users-card',
          },
          {
            id: 'configuration.users.invite',
            title: 'Invite user',
            description: 'Use this button to invite a user and choose their role.',
            selector: '.configuration-tab-users .configuration-users-invite-button',
          },
          {
            id: 'configuration.users.tables',
            title: 'Users and invitations',
            description: 'Use these tables to review active users and pending invitations.',
            selector: '.configuration-tab-users .configuration-users-tabs',
          },
        ];
      case '/configuration?tab=company':
        return [

          {
            id: 'configuration.tabs',
            title: 'Configuration tabs',
            description: 'Switch between profile, users, company, and subscription settings.',
            selector: '.configuration-tabs',
          },
          {
            id: 'configuration.company.card',
            title: 'Company management',
            description: 'This card contains company profile and company-level metric limit controls.',
            selector: '.configuration-tab-company .configuration-company-card',
          },
          {
            id: 'configuration.company.info',
            title: 'Company information',
            description: 'Review company metadata and update logo from this section.',
            selector: '.configuration-tab-company .configuration-company-info-section',
          },
          {
            id: 'configuration.company.logo',
            title: 'Logo update',
            description: 'Use this button to upload or replace the company logo.',
            selector: '.configuration-tab-company .configuration-company-logo-button',
          },
          {
            id: 'configuration.company.metric-limits',
            title: 'Metric limits',
            description: 'Configure company-specific metric warning ranges and overrides.',
            selector: '.configuration-tab-company .configuration-company-metric-limits',
          },
          {
            id: 'configuration.company.metric-table',
            title: 'Metric limits table',
            description: 'Each row can be edited to set min/max ranges for this company.',
            selector: '.configuration-tab-company .configuration-company-metric-table',
          },
        ];
      case '/configuration?tab=subscription':
        return [

          {
            id: 'configuration.tabs',
            title: 'Configuration tabs',
            description: 'Switch between profile, users, company, and subscription settings.',
            selector: '.configuration-tabs',
          },
          {
            id: 'configuration.subscription.card',
            title: 'Subscription overview',
            description: 'This section shows current subscription usage and trend context.',
            selector: '.configuration-tab-subscription .configuration-subscription-card',
          },
          {
            id: 'configuration.subscription.trend',
            title: 'Usage trend',
            description: 'Use the trend chart to understand consumption over time.',
            selector: '.configuration-tab-subscription .configuration-subscription-trend-card',
          },
          {
            id: 'configuration.subscription.filters',
            title: 'History filters',
            description: 'Adjust date range and granularity to inspect monthly or weekly usage.',
            selector: '.configuration-tab-subscription .configuration-subscription-range',
          },
          {
            id: 'configuration.subscription.metrics',
            title: 'Usage metric cards',
            description: 'Click metric cards to switch the trend focus to a specific limit type.',
            selector: '.configuration-tab-subscription .configuration-subscription-metrics-grid',
          },
        ];
      case '/configuration?tab=new-company':
        return [

          {
            id: 'configuration.tabs',
            title: 'Configuration tabs',
            description: 'Switch between profile, users, company, and subscription settings.',
            selector: '.configuration-tabs',
          },
          {
            id: 'configuration.new-company.card',
            title: 'Create new company',
            description: 'This tab explains the separate company creation process and prerequisites.',
            selector: '.configuration-tab-new-company .configuration-new-company-card',
          },
          {
            id: 'configuration.new-company.alert',
            title: 'Approval requirement',
            description: 'Sales approval is required before a new company is provisioned.',
            selector: '.configuration-tab-new-company .configuration-new-company-alert',
          },
          {
            id: 'configuration.new-company.start',
            title: 'Start process',
            description: 'Use this button to start the new company registration flow.',
            selector: '.configuration-tab-new-company .configuration-new-company-start-button',
          },
        ];
      case '/back-office?tab=subscriptions':
        return [
          {
            id: 'backoffice.subscriptions.table',
            title: 'Plans and assignments',
            description: 'This table is where plan rules and company/user assignment are controlled.',
            selector: '.ant-table-wrapper',
          },
        ];
      case '/back-office?tab=devices':
        return [
          {
            id: 'backoffice.devices.registry',
            title: 'Device registry',
            description: 'Use this panel to filter devices across companies and open the create or edit flow.',
            selector: '.backoffice-devices-card',
          },
        ];
      case '/back-office?tab=area-types':
        return [

          {
            id: 'backoffice.area-types.table',
            title: 'Area types list',
            description: 'Create, edit, or delete area types used in entity constraints.',
            selector: '.ant-table-wrapper',
          },
        ];
      case '/back-office?tab=ranges':
        return [
          {
            id: 'backoffice.ranges.defaults',
            title: 'Range defaults',
            description: 'Define reusable metric ranges that can be applied to each company without overwriting existing custom values.',
            selector: '.ant-card',
          },
        ];
      case '/back-office?tab=report':
        return [

          {
            id: 'backoffice.report.settings',
            title: 'Report settings panel',
            description: 'Adjust report options and defaults for all companies.',
            selector: '.report-settings-header',
          },
        ];
      case '/back-office?tab=agents':
        return [

          {
            id: 'backoffice.agents.panel',
            title: 'Agent context panel',
            description: 'Use this panel to update available agents and their runtime settings.',
            selector: '.ant-card',
          },
        ];
      default:
        return [];
    }
  }, []);

  const buildTourDefinitions = React.useCallback((routeKey: string, mode: 'auto' | 'manual'): TourStepDefinition[] => {
    const routeLabel = ROUTE_LABELS[routeKey] || 'Current section';
    const routeSpecificDefinitions = buildRouteSpecificTourDefinitions(routeKey);
    const sharedDefinitions: TourStepDefinition[] = mode === 'auto'
      ? (isMobile
          ? [
              {
                id: 'layout.mobile.menu-button',
                title: `${routeLabel} navigation`,
                description: 'Use this menu button to open navigation and switch sections.',
                selector: '.app-shell-menu-button',
              },
            ]
          : [
              {
                id: 'layout.sidebar.navigation',
                title: 'Sidebar navigation',
                description: 'Use this sidebar to move across available sections.',
                selector: '.sidebar-menu',
              },
              {
                id: 'layout.sidebar.actions',
                title: 'Quick actions',
                description: 'Change company, open settings, or replay the tour from here.',
                selector: '.sidebar-footer',
              },
            ])
      : [];

    if (showTrialBanner && mode === 'auto') {
      sharedDefinitions.push({
        id: 'layout.trial-banner',
        title: 'Trial status',
        description: 'This banner shows remaining trial days for the active company.',
        selector: '.app-shell-trial-banner',
      });
    }

    if (routeSpecificDefinitions.length === 0) {
      sharedDefinitions.push({
        id: 'layout.workspace-fallback',
        title: routeLabel,
        description: 'This is the main workspace for the current section.',
        selector: '.layout-content',
      });
    }

    return [...sharedDefinitions, ...routeSpecificDefinitions];
  }, [isMobile, showTrialBanner, buildRouteSpecificTourDefinitions]);

  const mapDefinitionsToActiveSteps = React.useCallback((definitions: TourStepDefinition[]): ActiveTourStep[] => {
    const active: ActiveTourStep[] = [];
    definitions.forEach((definition) => {
      const step: LayoutTourStep = {
        title: definition.title,
        description: definition.description,
        ...(definition.placement ? { placement: definition.placement } : {}),
      };
      if (definition.selector) {
        const targetElement = resolveTourTargetElement(definition.selector);
        if (!targetElement) return;
        step.target = () => targetElement;
      }
      active.push({
        id: definition.id,
        step,
      });
    });
    return active;
  }, []);

  React.useEffect(() => {
    if (!tourRouteKey || !onboardingStorageKey) return;
    const definitions = buildTourDefinitions(tourRouteKey, tourMode);
    const allSteps = mapDefinitionsToActiveSteps(definitions);
    markLoadedStepIds(allSteps.map((entry) => entry.id));
    if (allSteps.length === 0) {
      setTourOpen(false);
      setTourRouteKey(null);
      setActiveTourSteps([]);
      setTourCurrentStepIndex(0);
      tourShownStepIdsRef.current = new Set();
      tourCompletedStepIdsRef.current = new Set();
      return;
    }

    const state = readOnboardingState(onboardingStorageKey);
    const completedStepIds = new Set<string>(
      (state.completedStepIds && state.completedStepIds.length > 0)
        ? state.completedStepIds
        : state.seenStepIds || []
    );
    const filteredSteps = tourMode === 'manual'
      ? allSteps
      : allSteps.filter((entry) => !completedStepIds.has(entry.id));

    if (filteredSteps.length === 0) {
      markRouteSeen(tourRouteKey);
      setTourOpen(false);
      setTourRouteKey(null);
      setActiveTourSteps([]);
      setTourCurrentStepIndex(0);
      tourShownStepIdsRef.current = new Set();
      tourCompletedStepIdsRef.current = new Set();
      return;
    }

    setActiveTourSteps(filteredSteps);
    setTourCurrentStepIndex(0);
    tourShownStepIdsRef.current = new Set();
    tourCompletedStepIdsRef.current = new Set();
    setTourOpen(true);
  }, [
    tourRouteKey,
    onboardingStorageKey,
    tourMode,
    buildTourDefinitions,
    mapDefinitionsToActiveSteps,
    markLoadedStepIds,
    markRouteSeen,
  ]);

  const tourSteps = useMemo<LayoutTourStep[]>(
    () => activeTourSteps.map((entry) => entry.step),
    [activeTourSteps]
  );

  const markCurrentStepShown = React.useCallback((stepIndex: number) => {
    const stepId = activeTourSteps[stepIndex]?.id;
    if (!stepId) return;
    tourShownStepIdsRef.current.add(stepId);
  }, [activeTourSteps]);

  const markCurrentStepCompleted = React.useCallback((stepIndex: number) => {
    const stepId = activeTourSteps[stepIndex]?.id;
    if (!stepId) return;
    tourCompletedStepIdsRef.current.add(stepId);
  }, [activeTourSteps]);

  React.useEffect(() => {
    if (!tourOpen || activeTourSteps.length === 0) return;
    markCurrentStepShown(0);
  }, [tourOpen, activeTourSteps, markCurrentStepShown]);

  const handleTourChange = React.useCallback((nextCurrent: number) => {
    if (!tourOpen || activeTourSteps.length === 0) return;
    const normalizedNext = Math.max(0, Math.min(nextCurrent, activeTourSteps.length - 1));
    if (normalizedNext !== tourCurrentStepIndex) {
      markCurrentStepCompleted(tourCurrentStepIndex);
    }
    markCurrentStepShown(normalizedNext);
    setTourCurrentStepIndex(normalizedNext);
  }, [
    tourOpen,
    activeTourSteps,
    tourCurrentStepIndex,
    markCurrentStepCompleted,
    markCurrentStepShown,
  ]);

  const closeTour = React.useCallback((markSeen = true) => {
    if (markSeen && tourRouteKey) {
      const shownStepIds = Array.from(tourShownStepIdsRef.current);
      const completedStepIds = Array.from(
        new Set<string>([
          ...Array.from(tourCompletedStepIdsRef.current),
          ...shownStepIds,
        ])
      );
      markRouteSeen(tourRouteKey);
      markShownStepIds(shownStepIds);
      markCompletedStepIds(completedStepIds);
    }
    setTourOpen(false);
    setTourRouteKey(null);
    setActiveTourSteps([]);
    setTourCurrentStepIndex(0);
    tourShownStepIdsRef.current = new Set();
    tourCompletedStepIdsRef.current = new Set();
  }, [tourRouteKey, markRouteSeen, markShownStepIds, markCompletedStepIds]);

  React.useEffect(() => {
    if (helpModalOpen) return;
    setFailedHelpMediaIds({});
  }, [helpModalOpen, currentRouteKey]);

  const handleHelpMediaLoadError = React.useCallback((slideId: string) => {
    if (!slideId) return;
    setFailedHelpMediaIds((current) => {
      if (current[slideId]) return current;
      return { ...current, [slideId]: true };
    });
  }, []);

  const handleOnboardingSkip = () => {
    completeOnboardingWizard();
    setOnboardingOpen(false);
  };

  const handleOnboardingNext = () => {
    if (onboardingStepIndex >= onboardingLastStepIndex) {
      completeOnboardingWizard();
      setOnboardingOpen(false);
      return;
    }
    setOnboardingStepIndex((previous) => Math.min(previous + 1, onboardingLastStepIndex));
  };

  const handleOnboardingBack = () => {
    setOnboardingStepIndex((previous) => Math.max(previous - 1, 0));
  };

  const closeMobileNav = () => {
    if (isMobile) {
      setMobileNavOpen(false);
    }
  };

  const handleOpenHelpModal = () => {
    closeMobileNav();
    setFailedHelpMediaIds({});
    setHelpModalOpen(true);
  };

  const handleCloseHelpModal = () => {
    setHelpModalOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    closeMobileNav();
    navigate('/login');
  };

  const handleChangeCompany = () => {
    closeMobileNav();
    navigate('/select-company');
  };

  const handleSettings = () => {
    closeMobileNav();
    navigate('/configuration');
  };

  const handleReplayTour = () => {
    closeMobileNav();
    openTourForCurrentRoute();
  };

  const sidebarContent = (
    <div className="app-shell-sidebar-content">
      <div className={`sidebar-header${companyLogoUrl ? ' sidebar-header--has-logo' : ''}`}>
        <div className="app-shell-logo-container">
          {companyLogoUrl ? (
            <div className="app-shell-company-logo-box">
              <img src={companyLogoUrl} alt="Company logo" className="app-shell-company-logo" />
            </div>
          ) : (
            <img src={logoImage} alt="Logo" className="app-shell-logo-image" />
          )}
        </div>
        {!companyLogoUrl && (
          <div className="app-shell-company-name">
            {companyName}
          </div>
        )}
      </div>
      <Menu
        mode="inline"
        selectedKeys={[
          isBackOfficeRoute && backOfficeTab
            ? `/back-office?tab=${backOfficeTab}`
            : location.pathname,
        ]}
        items={menuItems}
        style={{ borderInlineEnd: 'none' }}
        className="sidebar-menu"
        onClick={closeMobileNav}
      />
      <div className="sidebar-footer">
        <Button
          type="text"
          icon={<SwapOutlined />}
          onClick={handleChangeCompany}
          className="change-company-button"
          block
        >
          Change Company
        </Button>
        {canViewSettings && (
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={handleSettings}
            className="settings-button"
            block
          >
            Settings
          </Button>
        )}
        <Button
          type="text"
          icon={<QuestionCircleOutlined />}
          onClick={handleReplayTour}
          className="tour-help-button"
          block
        >
          Replay tour
        </Button>
        <Button
          type="text"
          icon={<LogoutOutlined />}
          onClick={handleLogout}
          className="logout-button"
          block
        >
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <Layout className="app-shell">
      {!isMobile && (
        <Sider
          width={220}
          className="app-shell-sider"
        >
          {sidebarContent}
        </Sider>
      )}
      <Layout>
        {isMobile && (
          <Header className="app-shell-header">
            <div className="app-shell-header-left">
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setMobileNavOpen(true)}
                className="app-shell-menu-button"
              />
              <div className="app-shell-header-brand">
                {companyLogoUrl ? (
                  <div className="app-shell-header-logo-box">
                    <img src={companyLogoUrl} alt="Company logo" className="app-shell-header-logo" />
                  </div>
                ) : (
                  <span className="app-shell-header-name">{companyName}</span>
                )}
              </div>
            </div>
          </Header>
        )}
        {showTrialBanner && (
          <div className="app-shell-trial-banner">
            {currentTrialRemainingDays === 0
              ? `Trial expired for ${companyName}. Upgrade now to keep access.`
              : `Trial active for ${companyName}: ${currentTrialRemainingDays} day${currentTrialRemainingDays === 1 ? '' : 's'} remaining.`}
          </div>
        )}
        <Content className="app-shell-content">
          <main className="layout-content">
            {children}
          </main>
        </Content>
      </Layout>
      <Drawer
        open={mobileNavOpen}
        placement="left"
        width="80vw"
        onClose={closeMobileNav}
        className="app-shell-drawer"
      >
        {sidebarContent}
      </Drawer>
      <Button
        type="default"
        size="small"
        icon={<PlayCircleOutlined />}
        onClick={handleOpenHelpModal}
        className="floating-help-button"
        style={{
          top: floatingHelpTopOffset,
          right: floatingHelpRightOffset,
        }}
      >
        {!isMobile ? 'Help' : null}
      </Button>
      <Modal
        open={helpModalOpen}
        title={`How to use ${activeRouteLabel}`}
        width={isMobile ? '94vw' : 860}
        onCancel={handleCloseHelpModal}
        destroyOnClose
        footer={[
          <Button
            key="tour"
            onClick={() => {
              handleCloseHelpModal();
              openTourForCurrentRoute('manual');
            }}
          >
            Replay page tour
          </Button>,
          <Button key="close-help" type="primary" onClick={handleCloseHelpModal}>
            Close
          </Button>,
        ]}
      >
        <div className="route-help-intro">
          Short walkthrough clips for this view. Swipe or use arrows to move between steps.
        </div>
        <Carousel dots className="route-help-carousel">
          {activeHelpSlides.map((slide) => {
            const mediaKey = `${helpMediaGroupKey}:${slide.id}`;
            const failedToLoad = Boolean(failedHelpMediaIds[mediaKey]);

            return (
              <div key={slide.id} className="route-help-slide">
                <div className="route-help-media-frame">
                  {failedToLoad ? (
                    <div className="route-help-media-fallback">
                      <div className="route-help-media-fallback-title">Help media not found</div>
                      <div className="route-help-media-fallback-path">{slide.src}</div>
                    </div>
                  ) : slide.type === 'video' ? (
                    <video
                      className="route-help-media"
                      controls
                      autoPlay
                      loop
                      muted
                      playsInline
                      poster={slide.poster || HELP_MEDIA_FALLBACK_POSTER}
                      onError={() => handleHelpMediaLoadError(mediaKey)}
                    >
                      <source src={slide.src} type={slide.mimeType || 'video/webm'} />
                    </video>
                  ) : (
                    <img
                      src={slide.src}
                      alt={slide.title}
                      className="route-help-media"
                      onError={() => handleHelpMediaLoadError(mediaKey)}
                    />
                  )}
                </div>
                <div className="route-help-slide-copy">
                  <h4>{slide.title}</h4>
                  <p>{slide.description}</p>
                </div>
              </div>
            );
          })}
        </Carousel>
      </Modal>
      <Modal
        open={onboardingOpen}
        title="Workspace onboarding"
        width={680}
        maskClosable={false}
        closable={false}
        destroyOnClose={false}
        footer={[
          <Button key="skip" onClick={handleOnboardingSkip}>
            Skip
          </Button>,
          <Button
            key="back"
            onClick={handleOnboardingBack}
            disabled={onboardingStepIndex === 0}
          >
            Back
          </Button>,
          <Button key="next" type="primary" onClick={handleOnboardingNext}>
            {onboardingStepIndex >= onboardingLastStepIndex ? 'Finish' : 'Next'}
          </Button>,
        ]}
      >
        <Steps
          current={onboardingStepIndex}
          size="small"
          className="layout-onboarding-steps"
          items={onboardingSteps.map((step) => ({ title: step.title }))}
        />
        <div className="layout-onboarding-content">
          <h3>{onboardingCurrentStep?.title}</h3>
          <div>{onboardingCurrentStep?.description}</div>
          {onboardingCurrentStep?.actionLabel && onboardingCurrentStep?.onAction && (
            <Button
              type="link"
              onClick={onboardingCurrentStep.onAction}
              className="layout-onboarding-action"
            >
              {onboardingCurrentStep.actionLabel}
            </Button>
          )}
        </div>
      </Modal>
      <Tour
        open={tourOpen}
        current={tourCurrentStepIndex}
        onChange={handleTourChange}
        onClose={() => closeTour(true)}
        steps={tourSteps}
        zIndex={1200}
      />
    </Layout>
  );
};

export default LayoutComponent;
